import { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Paper, Button, Spinner, Alert, Chip, Tabs, EmptyState } from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { NEUTRAL_BTN } from '../lib/buttonStyles';
import {
  adminApiClient,
  AdminApiError,
  type CommentQueueItem,
  type CommentQueueFilter,
  type CommentQueueResult,
} from '../lib/adminApiClient';

/**
 * Recipe-comment moderation (comment:read · comment:moderate).
 *
 * 🛑 This screen is the ONLY path by which a reader ever sees a comment. They
 * land PENDING and the recipe-api reader query filters status:'approved', so
 * nothing here is cosmetic — an unreviewed queue means nothing is published,
 * and a careless approve puts user text on a page carrying our AdSense tags.
 *
 * Rejecting KEEPS the row (the server updates status, never deletes) so a
 * decision can be re-examined when an author disputes it.
 */

const FILTERS: Array<{ key: CommentQueueFilter; label: string }> = [
  { key: 'pending', label: 'Pending' },
  { key: 'reported', label: 'Reported' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

const REASON_LABEL: Record<string, string> = {
  spam: 'spam',
  offensive: 'offensive',
  off_topic: 'off topic',
  unsafe_advice: 'unsafe advice',
  other: 'other',
};

const ORANGE = '#f67a1a';

function Stars({ rating }: { rating: number | null }) {
  if (!rating) return null;
  return (
    <Box component="span" aria-label={`${rating} out of 5`} sx={{ color: '#e8a33d', fontSize: 14, letterSpacing: 1 }}>
      {'★'.repeat(rating)}
      <Box component="span" sx={{ color: '#d8ded9' }}>{'★'.repeat(5 - rating)}</Box>
    </Box>
  );
}

export default function CommentModeration() {
  const caps = useCapabilities();
  const canModerate = caps.has('comment:moderate');

  const [filter, setFilter] = useState<CommentQueueFilter>('pending');
  const [data, setData] = useState<CommentQueueResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Per-row so one slow request cannot disable the whole queue.
  const [busy, setBusy] = useState<Record<string, boolean>>({});

  const load = useCallback(async (status: CommentQueueFilter) => {
    setLoading(true);
    setError(null);
    try {
      setData(await adminApiClient.listComments({ status, limit: 50 }));
    } catch (e) {
      setError(e instanceof AdminApiError ? e.message : 'Could not load the comment queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(filter);
  }, [filter, load]);

  const act = async (c: CommentQueueItem, action: 'approve' | 'reject') => {
    setBusy((b) => ({ ...b, [c.commentId]: true }));
    setError(null);
    try {
      await adminApiClient.moderateComment(c.commentId, { action });
      // Refetch rather than splice: the counts across all four lenses move with
      // every decision, and a locally-patched list would show stale totals.
      await load(filter);
    } catch (e) {
      setError(e instanceof AdminApiError ? e.message : `Could not ${action} that comment`);
    } finally {
      setBusy((b) => ({ ...b, [c.commentId]: false }));
    }
  };

  const counts = data?.counts;
  const tabLabel = (f: { key: CommentQueueFilter; label: string }) => {
    const n = counts?.[f.key];
    return n ? `${f.label} (${n})` : f.label;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography component="h1" sx={{ fontSize: 24, fontWeight: 800, color: ORANGE, mb: 0.5 }}>
        Comment moderation
      </Typography>
      <Typography sx={{ fontSize: 14, color: '#5a7063', mb: 2 }}>
        Comments are invisible until approved. Approve publishes immediately; reject keeps the row for audit and
        never shows it.
      </Typography>

      {!canModerate && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can read this queue but not act on it — that needs the <code>comment:moderate</code> capability.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Tabs
        ariaLabel="Comment moderation queue"
        value={filter}
        onChange={(_e, v) => setFilter(v as CommentQueueFilter)}
        tabs={FILTERS.map((f) => ({ value: f.key, label: tabLabel(f) }))}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Spinner />
        </Box>
      ) : !data || data.comments.length === 0 ? (
        <EmptyState
          icon="messages"
          title={filter === 'pending' ? 'Nothing waiting' : 'Nothing here'}
          body={
            filter === 'pending'
              ? 'Every comment has been reviewed.'
              : 'No comments match this filter yet.'
          }
        />
      ) : (
        data.comments.map((c) => (
          <Paper key={c.commentId} sx={{ p: 2, mb: 1.5, border: '1px solid #e2ebe4' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Typography component="span" sx={{ fontWeight: 700, fontSize: 15 }}>
                {c.author}
              </Typography>
              <Stars rating={c.rating} />
              {c.recipeTitle && (
                <Typography component="span" sx={{ fontSize: 13, color: '#5a7063' }}>
                  on{' '}
                  <Box component="span" sx={{ color: '#25862d' }}>
                    {c.recipeTitle}
                  </Box>
                </Typography>
              )}
              <Typography component="span" sx={{ fontSize: 13, color: '#5a7063' }}>
                {new Date(c.createdAt).toLocaleString()}
              </Typography>
              {c.reportCount > 0 && (
                <Chip
                  label={`${c.reportCount} report${c.reportCount === 1 ? '' : 's'}${
                    c.reasons.length ? ` · ${c.reasons.map((r) => REASON_LABEL[r] ?? r).join(', ')}` : ''
                  }`}
                  sx={{ backgroundColor: '#fdecea', color: '#b3261e', fontWeight: 700 }}
                />
              )}
              {filter !== 'pending' && (
                <Chip label={c.status} sx={{ backgroundColor: '#eef2ef', color: '#5a7063' }} />
              )}
            </Box>

            <Typography sx={{ fontSize: 14, lineHeight: 1.6, mb: 1.5, whiteSpace: 'pre-wrap' }}>{c.body}</Typography>

            {canModerate && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  ariaLabel={`Approve comment by ${c.author}`}
                  buttonText="Approve"
                  onClick={() => void act(c, 'approve')}
                  disabled={busy[c.commentId] || c.status === 'approved'}
                  sx={{ backgroundColor: '#25862d', color: '#fff' }}
                />
                <Button
                  variant="outlined"
                  ariaLabel={`Reject comment by ${c.author}`}
                  buttonText="Reject"
                  onClick={() => void act(c, 'reject')}
                  disabled={busy[c.commentId] || c.status === 'rejected'}
                  sx={{ ...NEUTRAL_BTN, color: '#b3261e', borderColor: '#b3261e' }}
                />
                {c.recipeId && (
                  <Button
                    variant="outlined"
                    ariaLabel="Open the recipe in a new tab"
                    buttonText="View recipe"
                    sx={NEUTRAL_BTN}
                    onClick={() => void window.open(`/recipes/${c.recipeId}`, '_blank', 'noopener')}
                  />
                )}
              </Box>
            )}
          </Paper>
        ))
      )}
    </Box>
  );
}
